import { CircleFill } from "@carbon/icons-react";
import { Tag, Link } from "@carbon/react";
import { isDefined, isNotFalsy } from "@flaminem/utils";
import get from "lodash/get";
import { FC, useCallback } from "react";

import { AlertDecisionTag } from "src/components/Alerts/AlertDecisionTag";
import { CaseTypeTag } from "src/components/CaseTypeTag";
import { Highlight } from "src/components/Highlight/Highlight.tsx";
import { TableTruncate } from "src/components/Tables";
import { ConcatenationWidget } from "src/dynamic-view-renderer/headlessWidgets/ConcatenationWidget.tsx";
import { findTargetElement } from "src/dynamic-view-renderer/Widgets/WidgetProperties/widgetProperties.utils";
import { useFormatPropertyValue } from "src/hooks/property/useFormatProperty";
import { useFormatDate } from "src/internationalization/formatters";
import { getFullName } from "src/services/user/getUserFullName";
import { CaseForCustomer } from "src/types/customer";

import { BaseHeadlessWidget, HeadlessWidgetProps } from "./HeadlessWidget.typings";

export const HeadlessWidgetRenderer: FC<HeadlessWidgetProps> = (props) => {
  const { widget, data, onClick, searchTerm } = props;
  const formatDate = useFormatDate();
  const formatPropertyValue = useFormatPropertyValue();

  const getRootValue = useCallback(
    (path: string): string | undefined => {
      const value = get(data.root, path);
      return value !== undefined && value !== null ? String(value) : undefined;
    },
    [data.root]
  );

  const getPropertyValuesArray = useCallback(() => {
    const baseWidget = widget as BaseHeadlessWidget;
    const targetElement = findTargetElement(data.elements, baseWidget.elementPath);

    return targetElement && "properties" in targetElement
      ? targetElement.properties
      : data.propertyValues;
  }, [widget, data.elements, data.propertyValues]);

  const getPropertyValues = useCallback((): string[] => {
    const baseWidget = widget as BaseHeadlessWidget;
    const propertyValues = getPropertyValuesArray();

    if (!propertyValues) return [];

    return baseWidget.properties
      .map((prop) => {
        const propertyValue = propertyValues.find((pv) => pv.property.code === prop);
        const value = propertyValue?.value?.value;
        return isDefined(value) ? String(value) : "";
      })
      .filter(isNotFalsy);
  }, [widget, getPropertyValuesArray]);

  const getPropertyValue = useCallback(
    (propertyCode: string) => {
      const propertyValues = getPropertyValuesArray();

      if (!propertyValues) return null;

      return propertyValues.find((pv) => pv.property.code === propertyCode) || null;
    },
    [getPropertyValuesArray]
  );

  const renderFormattedProperty = useCallback(
    (propertyCode?: string | null) => {
      if (!propertyCode) return null;

      const propertyValue = getPropertyValue(propertyCode);

      if (!propertyValue) return null;

      const formattedValue = formatPropertyValue(propertyValue);
      if (propertyCode === "id" && searchTerm) {
        return <Highlight term={searchTerm} text={formattedValue} className="truncate text-sm" />;
      }

      return <div className="truncate">{formattedValue}</div>;
    },
    [getPropertyValue, formatPropertyValue, searchTerm]
  );

  switch (widget.type) {
    case "concatenation": {
      return <ConcatenationWidget widget={widget} data={data} searchTerm={searchTerm} />;
    }
    case "boolean": {
      const caseData = data as CaseForCustomer;
      return caseData.hasAlert ? <CircleFill /> : "-";
    }

    case "date": {
      const baseWidget = widget as BaseHeadlessWidget & {
        dateStyle?: "full" | "long" | "medium" | "short";
      };
      const propertyCode = baseWidget.properties[0];

      const formattedProperty = renderFormattedProperty(propertyCode);

      if (formattedProperty) return formattedProperty;

      const value = baseWidget.elementPath ? getPropertyValues()[0] : getRootValue(propertyCode);
      if (!value) return null;
      return <>{formatDate(new Date(value), { dateStyle: baseWidget.dateStyle || "medium" })}</>;
    }

    case "number": {
      const baseWidget = widget as BaseHeadlessWidget;
      const propertyCode = baseWidget.properties[0];

      const formattedProperty = renderFormattedProperty(propertyCode);
      if (formattedProperty) return formattedProperty;

      const value = baseWidget.elementPath ? getPropertyValues()[0] : getRootValue(propertyCode);
      return <>{value || ""}</>;
    }

    case "property": {
      const baseWidget = widget as BaseHeadlessWidget;

      const propertyCode = baseWidget.properties[0];
      const renderPropertty = renderFormattedProperty(propertyCode);
      return renderPropertty ?? "-";
    }

    case "score": {
      const value = getRootValue(widget.properties[0]);
      return value ? <Tag type={widget.tagType || "magenta"}>{value}</Tag> : null;
    }

    case "scenario": {
      const baseWidget = widget as BaseHeadlessWidget & { truncateLines?: number };
      const value = getRootValue(baseWidget.properties[0]);
      return value ? (
        <TableTruncate lines={baseWidget.truncateLines || 1} label={value}>
          <Link
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onClick?.();
            }}
          >
            {value}
          </Link>
        </TableTruncate>
      ) : null;
    }

    case "assignee": {
      const assignee = get(data.root, "assignee");
      return <div className="truncate">{assignee ? getFullName(assignee) : ""}</div>;
    }

    case "decision": {
      const decision = get(data.root, "decision");
      if (!decision) return null;

      const root = data.root as
        | { financialLossAmount?: number | null; preventedFraudAmount?: number | null }
        | undefined;
      return (
        <div className="inline-flex max-w-fit">
          <AlertDecisionTag
            decision={decision}
            financialLossAmount={root?.financialLossAmount}
            preventedFraudAmount={root?.preventedFraudAmount}
          />
        </div>
      );
    }

    case "caseType": {
      const baseWidget = widget as BaseHeadlessWidget;
      const propertyCode = baseWidget.properties[0];

      const propertyValue = getPropertyValue(propertyCode);
      let caseTypeCode: string | undefined;

      if (propertyValue) {
        const formattedValue = formatPropertyValue(propertyValue);
        caseTypeCode = String(formattedValue);
      } else {
        const root = data.root as
          | { currentCase?: { typeCode?: string }; typeCode?: string }
          | undefined;
        caseTypeCode = root?.currentCase?.typeCode || root?.typeCode;
      }

      if (!caseTypeCode) return null;

      return (
        <CaseTypeTag
          caseTypeCode={caseTypeCode}
          caseTypeDefinitions={data.caseTypeDefinitions}
          fallbackType="magenta"
        />
      );
    }
    case "nbrOfOpenAlerts": {
      const nbrOfOpenAlerts = get(data.root, "nbrOfOpenAlerts");
      if (nbrOfOpenAlerts === null) return null;

      return <Tag type="magenta">{nbrOfOpenAlerts}</Tag>;
    }

    default:
      return null;
  }
};
